import {
  Button,
  Checkbox,
  CheckboxStates,
  FilterContext,
  Panel,
  PanelBody,
  PanelRow,
  Select,
  Table,
} from '@teamsnap/teamsnap-ui';
import ComboBox from '@teamsnap/teamsnap-ui/dist/js/components/ComboBox/Combobox';
import {
  PaginationButtons,
  PaginationCurrentSubsetDisplay,
  PaginationSelect,
} from '@teamsnap/teamsnap-ui/dist/js/components/Pagination';
import {
  convertObjsToValueLabel,
  getCheckboxStateForBulkActions,
} from '@teamsnap/teamsnap-ui/dist/js/components/Table/Paginated/helpers';
import { Placement } from '@teamsnap/teamsnap-ui/dist/js/types/placement';
import { exportToCsv } from '@teamsnap/teamsnap-ui/dist/js/utils/export';
import { chunk, dropRight, isNil, uniqBy } from 'lodash';
import * as React from 'react';
import BasicSearch from './basic-search';
import DateFilterComponent, { FilterValue } from './date-filter';
import ExpandingTable from './expanding-table';
import './paginated-table.scss';

export interface BulkAction {
  label: string;
  onSelected: (selected: any) => void;
  disabled?: boolean;
}

export type Column = {
  name: string;
  label: string | React.ReactElement;
  mods?: string;
  isSortable?: boolean;
  align?: string;
  selected?: boolean;
  style?: React.CSSProperties;
  isFile?: boolean;
};

const SelectFilter = (
  fieldName: string,
  label: string,
  options?:
    | { [key: string]: string | React.ReactNode }
    | { value: string; label: string; subtext?: string }[],
  tooltip?: JSX.Element,
  tooltipLink?: string,
  disabled = false,
  onHoverText?: string
) => {
  const SelectFilterHOC = ({ isLast }: { isLast: boolean }) => {
    const ctx = React.useContext(FilterContext);

    const onChange = (values) => {
      if (values?.length > 0 || values?.kind) {
        ctx.setActiveFilters({ ...ctx.activeFilters, [fieldName]: values });
      } else {
        delete ctx.activeFilters[fieldName];
        ctx.setActiveFilters({ ...ctx.activeFilters });
      }
    };

    return (
      <ComboBox
        disabled={disabled}
        mods={isLast ? '' : 'u-spaceRightSm'}
        onChange={onChange}
        selected={ctx.activeFilters[fieldName]}
        name={fieldName}
        buttonLabel={label}
        tooltip={tooltip}
        tooltipLink={tooltipLink}
        onHoverText={onHoverText}
        items={
          options.length
            ? (options as { value: string; label: string; subtext?: string }[])
            : convertObjsToValueLabel(
                options as { [key: string]: string | React.ReactNode }
              )
        }
      />
    );
  };
  return SelectFilterHOC;
};

const DateFilter = (
  fieldName: string,
  label: string,
  noDateLabel?: string,
  yearPlaceholder?: string,
  rangeMin?: string,
  rangeMax?: string
) => {
  const DateFilterHOC = ({ isLast }: { isLast: boolean }) => {
    const ctx = React.useContext(FilterContext);

    const onChange = (value?: FilterValue) => {
      ctx.setActiveFilters({ ...ctx.activeFilters, [fieldName]: value });
    };

    return (
      <DateFilterComponent
        mods={isLast ? '' : 'u-spaceRightSm'}
        onChange={onChange}
        name={fieldName}
        title={label}
        value={ctx.activeFilters[fieldName]}
        noDateLabel={noDateLabel}
        yearPlaceholder={yearPlaceholder}
        rangeMin={rangeMin}
        rangeMax={rangeMax}
      />
    );
  };
  return DateFilterHOC;
};

type PaginatedTableProps = {
  tableId?: string;
  bulkActions?: BulkAction[];
  customSearchFilter?: any;
  openFiltersRow?: boolean;
  filters?: Function[];
  isLoading?: boolean;
  searchPlaceholder?: string;
  noResultsText?: string;
  onSelectRows?: (x: { id: string }[]) => void;
  shouldClearSelectedRows?: boolean;
  onExport?: Function;
  fullRowSelect?: boolean;
  paginationPlacement?:
    | Placement.Top
    | Placement.Bottom
    | Placement.RightBottom;
  rows: { id: string; disabled?: boolean }[];
  activeFilters?: object;
  setActiveFilters?: (x: object) => any;
  pageSizeOptions?: number[];
  searchTerm?: string;
  setSearchTerm?: (x: string) => any;
  columns: Column[];
  onSelectColumns?: (x: string[]) => void;
  style?: React.CSSProperties;
  sorting: {
    setSortAscending: (x: boolean) => any;
    setSortBy: (x: string) => any;
    sortBy: string;
    sortAscending: boolean;
  };
  pagination: {
    totalItems: number;
    itemsPerPage?: number;
    setItemsPerPage?: (x: number) => any;
    currentPage: number;
    setCurrentPage: (x: number) => any;
  };
  isExpandable?: boolean;
  bulkActionsOnAllSelected?: boolean;
  onOpenColumns?: () => void;
};

const DEFAULT_PAGESIZE_OPTIONS = [10, 25, 50];

const emptyObj = {};

const PaginatedTable = ({
  tableId,
  columns,
  onSelectColumns,
  customSearchFilter,
  searchPlaceholder,
  onSelectRows,
  shouldClearSelectedRows,
  isLoading,
  rows,
  searchTerm,
  setSearchTerm,
  pagination,
  sorting,
  style,
  setActiveFilters,
  activeFilters = emptyObj,
  bulkActions = [],
  filters = [],
  onExport = null,
  fullRowSelect = false,
  openFiltersRow = false,
  noResultsText = 'No results found',
  pageSizeOptions = DEFAULT_PAGESIZE_OPTIONS,
  paginationPlacement = Placement.RightBottom,
  isExpandable,
  bulkActionsOnAllSelected = false,
  onOpenColumns,
}: PaginatedTableProps) => {
  const [selected, setSelected] = React.useState([]);
  const [filterOpen, setFilterOpen] = React.useState(openFiltersRow);
  const [isResettingFilters, setIsResettingFilters] = React.useState(false);
  const {
    totalItems,
    itemsPerPage = 50,
    setItemsPerPage,
    currentPage,
    setCurrentPage,
  } = pagination;
  const filterLength = Object.values(activeFilters).filter(
    (x) => !isNil(x) && x !== ''
  ).length;
  const shouldDisplayPaginationAtBottom =
    paginationPlacement === Placement.Bottom ||
    paginationPlacement === Placement.RightBottom;
  const shouldPaginateAtTop =
    !shouldDisplayPaginationAtBottom && filters.length === 0;
  const includesSearch = searchTerm || searchTerm === '';
  const { setSortAscending, setSortBy, sortBy, sortAscending } = sorting;
  const [columnsFlyoutOpen, setColumnsFlyoutOpen] = React.useState(false);
  const [initialized, setInitialized] = React.useState(false);
  const [selectedColumns, setSelectedColumns] = React.useState<string[]>([]);

  React.useEffect(() => {
    setInitialized(true);
    setSelectedColumns(columns.filter((x) => x.selected).map((x) => x.name));
  }, []);

  React.useEffect(() => {
    setSelectedColumns(columns.filter((x) => x.selected).map((x) => x.name));
  }, [columns]);

  const handleBodyClick = React.useCallback((e) => {
    e.stopPropagation();
    const isTargetingPopup = e.target.closest('#manage-columns') !== null;
    if (!isTargetingPopup) {
      setColumnsFlyoutOpen(false);
    }
  }, []);

  React.useEffect(() => {
    if (!columnsFlyoutOpen) {
      if (
        initialized &&
        columns.some(
          (column) =>
            (column.selected && !selectedColumns.includes(column.name)) ||
            (selectedColumns.includes(column.name) && !column.selected)
        )
      ) {
        onSelectColumns(selectedColumns);
      }

      window.removeEventListener('click', handleBodyClick, false);
    } else {
      window.addEventListener('click', handleBodyClick, false);
    }
  }, [columnsFlyoutOpen]);

  React.useEffect(() => {
    if (shouldClearSelectedRows) setSelected([]);
  }, [shouldClearSelectedRows]);

  React.useEffect(() => {
    setFilterOpen(openFiltersRow);
  }, [setFilterOpen, openFiltersRow]);

  const setNewItemsPerPage = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const getCheckboxStateForAllBulkActions = (selected: any[], rows: any[]) => {
    let checkboxState = CheckboxStates.FALSE;
    if (selected.length === 0) {
      return CheckboxStates.FALSE;
    }

    const rowIds = rows.map((r) => r.id);
    const selectedIds = selected.map((r) => r.id);
    const allThere = rowIds.every((id) => selectedIds.includes(id));

    if (allThere) {
      checkboxState = CheckboxStates.TRUE;
    }
    return checkboxState;
  };

  const handleManageColumnsClick = () => {
    setColumnsFlyoutOpen(!columnsFlyoutOpen);
    if (onOpenColumns) {
      onOpenColumns();
    }
  };

  const columnsWithoutActions = dropRight(columns);
  let cols = columns.filter((x) => x.selected || x.selected === undefined);
  const checkboxState = !bulkActionsOnAllSelected
    ? getCheckboxStateForBulkActions(selected, rows)
    : getCheckboxStateForAllBulkActions(selected, rows);
  if (onSelectRows) {
    cols = [
      {
        name: 'selected',
        label: (
          <div>
            <Checkbox
              name="select-all"
              mods="u-padBottomNone"
              inputProps={{
                checked: checkboxState,
                onChange: () => {
                  if (!bulkActionsOnAllSelected) {
                    // if there is at least one row checked, clear all
                    if (checkboxState !== CheckboxStates.FALSE) {
                      setSelected([]);
                    } else {
                      const rowsSelectable = rows.filter(
                        (row) => !row.disabled
                      );
                      // select all rows if none are checked
                      setSelected(rowsSelectable);
                    }
                  } else {
                    if (checkboxState !== CheckboxStates.FALSE) {
                      setSelected(
                        selected.filter(
                          (row) => !rows.map((r) => r.id).includes(row.id)
                        )
                      );
                    } else {
                      const rowsSelectable = rows.filter(
                        (row) => !row.disabled
                      );
                      setSelected(
                        uniqBy(
                          [...selected, ...rowsSelectable],
                          (obj) => obj.id
                        )
                      );
                    }
                  }
                },
              }}
            />
          </div>
        ),
        mods: 'u-size1of12',
      },
      ...cols,
    ];

    // use IDs as keys to determine uniqueness
    const selectedids = selected.map((e) => e.id);
    rows = rows.map((ele) => {
      const rowData = {
        ...ele,
        selected: (
          <div>
            <Checkbox
              name={`select-${ele.id}`}
              mods="u-padBottomNone"
              inputProps={{
                disabled: ele.disabled ? true : null,
                checked: selectedids.includes(ele.id),
                onChange: () => {},
                onClick: () => {
                  if (selectedids.includes(ele.id)) {
                    setSelected(selected.filter((e) => e.id !== ele.id));
                  } else {
                    setSelected([...selected, ele]);
                  }
                },
              }}
            />
          </div>
        ),
      };

      if (rowData.selected && fullRowSelect) {
        rowData['onClick'] = () => {
          if (selectedids.includes(ele.id)) {
            setSelected(selected.filter((e) => e.id !== ele.id));
          } else {
            setSelected([...selected, ele]);
          }
        };
      }
      return rowData;
    });
  }

  // collisions overwrite
  const bulkActionFuncsByLabel = bulkActions?.reduce(
    (acc, action: BulkAction) => {
      acc[action.label] = action.onSelected;
      return acc;
    },
    {}
  );

  const updateSearchFilter = ({ searchTerm: search }) => {
    setCurrentPage(1);
    setSearchTerm(search);
  };

  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeFilters]);

  React.useEffect(() => {
    if (isResettingFilters) setIsResettingFilters(false);
  }, [isResettingFilters]);

  React.useEffect(() => {
    if (onSelectRows) {
      onSelectRows(selected);
    }
  }, [selected]);

  React.useEffect(() => {
    if (isLoading) {
      setSelected([]);
    }
  }, [isLoading]);

  const paginationItems = (
    <div
      className={`Grid-cell u-flex u-flexJustifyEnd ${
        !shouldPaginateAtTop ? 'u-sizeFill u-sizeFull' : 'u-sizeFit'
      }`}
    >
      <div
        data-testid="PaginatedTable--pagination-subset-display"
        className="u-spaceAuto u-spaceRightSm"
      >
        <PaginationCurrentSubsetDisplay
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          totalItems={totalItems}
        />
      </div>
      <PaginationButtons
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        mods={
          paginationPlacement === Placement.Bottom
            ? 'u-flexJustifyCenter u-flexGrow1'
            : ''
        }
      />
      {setItemsPerPage && (
        <div className="u-spaceLeftSm">
          <PaginationSelect
            options={pageSizeOptions}
            setItemsPerPage={setNewItemsPerPage}
            itemsPerPage={itemsPerPage}
          />
        </div>
      )}
    </div>
  );

  const filtersSection = (
    <>
      <div className="u-size7of8">
        {filters.map((Item, index) => (
          <Item key={index} isLast={index === filters.length - 1} />
        ))}
      </div>
      <div className="u-size1of8 u-textRight u-spaceRightMd">
        <Button
          testId="PaginatedTable--filter-button-clear-all"
          type="text"
          onClick={() => {
            setActiveFilters({});
            // This is a bit weird.
            // We were seeing issues updating the components successfully when clearing out all the filter
            // values. This, paired with the condition in the filter rendering, forces the filter section
            // to rerender when we clear out all the filters, ensuring that its rendering with the "freshest"
            // of values from the context.
            setIsResettingFilters(true);
          }}
        >
          Clear All
        </Button>
      </div>
    </>
  );

  return (
    <div className="Grid">
      <div className="Grid Grid-cell u-spaceTopSm">
        {bulkActions?.length > 0 ? (
          <div className="Grid-cell u-spaceRightSm u-flex u-size1of1 u-md-size2of12 u-flexJustifyStart">
            <Select
              inputProps={{
                value: '',
                onChange: (event) => {
                  const fn = bulkActionFuncsByLabel[event.target.value];
                  if (fn) {
                    fn(selected);
                  }
                },
              }}
              name="bulkActions"
              options={[
                {
                  label:
                    selected.length > 0
                      ? `${selected.length} selected`
                      : 'Actions',
                  value: null,
                },
                ...bulkActions.map((e) => ({
                  label: e.label,
                  value: e.label,
                  disabled: e.disabled || false,
                })),
              ]}
            />
          </div>
        ) : null}
        <div className="Grid-cell u-sizeFill u-md-size1of4">
          {customSearchFilter && customSearchFilter}
          {includesSearch && (
            <BasicSearch
              searchPlaceholder={searchPlaceholder}
              searchFunction={updateSearchFilter}
              searchValue={searchTerm}
            />
          )}
        </div>

        {shouldPaginateAtTop && paginationItems}

        {filters.length > 0 && (
          <div>
            <Button
              testId="PaginatedTable--filter-button"
              isActive={filterLength > 0 || filterOpen}
              onClick={() => {
                setFilterOpen(!filterOpen);
              }}
              mods="Button Button--primary u-spaceLeftSm"
              icon="wrench"
            >
              Filter{' '}
              {filterLength > 0 ? (
                <span
                  className="u-bgPrimary7 u-colorNeutral1 u-fontSizeXs"
                  style={{ borderRadius: '50px', padding: '1px 4px' }}
                >
                  {filterLength}
                </span>
              ) : null}
            </Button>
          </div>
        )}

        {onSelectColumns && (
          <div id="manage-columns">
            <Button
              testId="PaginatedTable--manage-columns-button"
              isActive={columnsFlyoutOpen}
              onClick={handleManageColumnsClick}
              mods="Button u-spaceLeftSm"
              icon="settings"
            >
              Manage Columns
            </Button>

            <Panel
              mods={`manage-columns-flyout ${
                !columnsFlyoutOpen ? 'u-hidden' : ''
              }`}
              data-testid="manage-columns-flyout"
            >
              <PanelBody>
                <PanelRow mods="Grid-cell u-flexAuto u-padBottomMd">
                  <h4 className="title">Manage Columns</h4>
                  <div className="u-flex">
                    {chunk(
                      columnsWithoutActions,
                      Math.round(columns.length / 3)
                    ).map((chunks, index) => (
                      <div className="columns-section" key={index}>
                        {chunks.map(({ name, label, selected }) => (
                          <Checkbox
                            key={`${name}-combobox`}
                            name={`${name}-combobox`}
                            label={label}
                            inputProps={{
                              disabled: selected === undefined,
                              checked:
                                selected === undefined ||
                                selectedColumns.includes(name),
                              value: name,
                              onChange: () => {
                                if (selectedColumns.includes(name)) {
                                  setSelectedColumns(
                                    selectedColumns.filter(
                                      (selectedFilter) =>
                                        selectedFilter !== name
                                    )
                                  );
                                } else {
                                  setSelectedColumns([
                                    ...selectedColumns,
                                    name,
                                  ]);
                                }
                              },
                            }}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </PanelRow>
              </PanelBody>
            </Panel>
          </div>
        )}

        {onExport && (
          <div data-testid="PaginatedTable--export-button">
            <Button
              isActive={rows.length > 0}
              onClick={() => {
                onExport({
                  exportToCsv,
                  page: currentPage,
                  itemsPerPage,
                  sortBy: sortBy,
                  sortAsc: sortAscending,
                  filter:
                    includesSearch || activeFilters
                      ? { searchTerm, ...activeFilters }
                      : null,
                });
              }}
              mods="u-spaceLeftSm"
              icon="import"
            >
              Export
            </Button>
          </div>
        )}
      </div>
      <Panel
        mods={`${
          filterOpen ? '' : 'u-hidden'
        } u-padSm u-spaceTopSm u-borderNeutral4 u-bgNeutral1 Grid-cell u-flex`}
      >
        <FilterContext.Provider value={{ activeFilters, setActiveFilters }}>
          {filtersSection}
        </FilterContext.Provider>
      </Panel>
      <div className="Grid-cell u-spaceTopSm">
        {isExpandable ? (
          <ExpandingTable
            columns={cols}
            rows={rows}
            defaultSort={`${sortAscending ? '-' : ''}${sortBy}`}
            externalSortingFunction={(name, isAscending) => {
              setSortBy(name);
              setSortAscending(isAscending);
            }}
            isLoading={isLoading}
            placeHolder={noResultsText}
            style={style}
            otherProps={{ id: tableId }}
          />
        ) : (
          <Table
            columns={cols}
            rows={rows}
            defaultSort={`${sortAscending ? '-' : ''}${sortBy}`}
            externalSortingFunction={(name, isAscending) => {
              setSortBy(name);
              setSortAscending(isAscending);
            }}
            isLoading={isLoading}
            placeHolder={noResultsText}
            style={style}
            otherProps={{ id: tableId }}
          />
        )}
      </div>
      {shouldDisplayPaginationAtBottom && paginationItems}
    </div>
  );
};

PaginatedTable.SelectFilter = SelectFilter;
PaginatedTable.DateFilter = DateFilter;

export default PaginatedTable;
